import React, { useState, useEffect, useRef } from "react";
import {
  UploadCloud,
  FileText,
  CheckCircle,
  AlertCircle,
  Download,
  MessageSquare,
  RefreshCw,
  File,
  ChevronDown,
  Loader2
} from "lucide-react";

const DocumentDashboard = () => {
  const [hoveredUpload, setHoveredUpload] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle selected files (you can later send them to your backend)
  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      console.log("Selected files:", files);
      // Here you can upload files to your server
      // Example: uploadFiles(files);
      alert(`${files.length} file(s) selected! Ready for upload.`);
    }
  };

  const smartChecks = [
    { title: "HRA Proof Frequency", desc: "All 12 months covered", status: "success" },
    { title: "Lender Certificate", desc: "Valid & matches PAN", status: "success" },
    { title: "Name/PAN/Date Match", desc: "Exact match found", status: "warning" },
    { title: "Amount Match", desc: "Transaction matched", desc2: "Difference: ₹1,250", status: "warning" },
    { title: "Section Coverage", desc: "All claimed sections have proofs", desc2: "80C, 80D, 80E, HRA", status: "success" },
  ];

  const getStatusIcon = (status) => {
    return status === "success"
      ? <CheckCircle style={{ color: "#86efac" }} size={20} />
      : <AlertCircle style={{ color: "#fde047" }} size={20} />;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch('/api/getReviewQueueDashboard');

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status}`);
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          setDocuments(data);
        } else if (data.documents && Array.isArray(data.documents)) {
          setDocuments(data.documents);
        } else {
          throw new Error("Invalid data format");
        }
      } catch (err) {
        console.error("Error fetching dashboard:", err);
        setError(err.message || "Failed to load documents.");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#0a0a1a", color: "#e0e0e0" }}>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        multiple
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: "32px", maxWidth: "1600px", margin: "0 auto", padding: "32px 24px" }}>
        
        {/* Main Content */}
        <div>
          <h2 style={{ fontSize: "24px", fontWeight: "bold", color: "#ffffff", marginBottom: "8px" }}>
            Documents Review & Verification
          </h2>
          <p style={{ fontSize: "16px", color: "#9ca3af", marginBottom: "32px" }}>
            Review uploaded proofs and validate OCR-extracted data
          </p>

          {/* Upload Zone - NOW FULLY CLICKABLE */}
          <div
            onClick={handleUploadClick}
            onMouseEnter={() => setHoveredUpload(true)}
            onMouseLeave={() => setHoveredUpload(false)}
            style={{
              backgroundColor: hoveredUpload ? "#11111f" : "#0f0f1a",
              border: "2px dashed #4b5563",
              borderRadius: "16px",
              padding: "64px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              marginBottom: "32px"
            }}
          >
            <UploadCloud size={48} style={{ color: "#6b7280", margin: "0 auto 16px auto", display: "block" }} />
            <p style={{ color: "#e5e7eb", fontWeight: "500", fontSize: "18px", marginBottom: "8px" }}>
              Drag & drop files here or click to browse
            </p>
            <p style={{ fontSize: "14px", color: "#6b7280" }}>
              Supported: PDF, JPG, PNG • Max 10MB per file
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // Prevent double trigger
                handleUploadClick();
              }}
              style={{
                backgroundColor: "#9333ea",
                color: "#ffffff",
                padding: "12px 32px",
                borderRadius: "9999px",
                fontWeight: "600",
                marginTop: "24px",
                border: "none",
                cursor: "pointer"
              }}
            >
              + Upload Documents
            </button>
          </div>

          {/* Documents Grid */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "64px" }}>
              <Loader2 size={48} style={{ color: "#9333ea", animation: "spin 1s linear infinite" }} />
              <p style={{ color: "#9ca3af", marginTop: "16px" }}>Loading documents...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "64px", color: "#ef4444" }}>
              <AlertCircle size={48} />
              <p style={{ marginTop: "16px" }}>{error}</p>
            </div>
          ) : documents.length === 0 ? (
            <div style={{ textAlign: "center", padding: "64px", color: "#9ca3af" }}>
              <FileText size={48} />
              <p style={{ marginTop: "16px" }}>No documents found.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "24px" }}>
              {documents.map((doc, i) => (
                <div
                  key={i}
                  style={{
                    background: doc.gradient ? `linear-gradient(to bottom right, ${doc.gradient.split(' ')[1]}, ${doc.gradient.split(' ')[3]})` : "#1f2937",
                    borderRadius: "16px",
                    overflow: "hidden",
                    padding: "24px",
                    position: "relative",
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.5)"
                  }}
                >
                  {doc.verified && (
                    <div style={{
                      position: "absolute",
                      top: "16px",
                      right: "16px",
                      backgroundColor: "rgba(0,0,0,0.4)",
                      backdropFilter: "blur(4px)",
                      padding: "6px 12px",
                      borderRadius: "9999px",
                      fontSize: "12px",
                      color: "#86efac",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}>
                      <CheckCircle size={14} />
                      Verified
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
                    <div style={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(8px)",
                      borderRadius: "16px",
                      padding: "32px"
                    }}>
                      <FileText size={64} style={{ color: "#ffffff" }} />
                      <span style={{ display: "block", textAlign: "center", color: "#ffffffb3", fontSize: "12px", marginTop: "8px" }}>
                        PDF
                      </span>
                    </div>
                  </div>

                  <h4 style={{ fontSize: "16px", fontWeight: "600", textAlign: "center", color: "#ffffff", marginBottom: "16px" }}>
                    {doc.name || "Untitled Document"}
                  </h4>

                  <div style={{ fontSize: "14px", color: "#e5e7eb" }}>
                    <p style={{ marginBottom: "8px" }}>
                      Uploaded by: <span style={{ fontWeight: "500" }}>{doc.uploadedBy || "Unknown"}</span>
                    </p>
                    <p>
                      Trust Score: <span style={{ color: "#86efac", fontWeight: "bold" }}>
                        {doc.trustScore !== undefined ? `${doc.trustScore}%` : "N/A"}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div style={{ position: "sticky", top: "32px", height: "fit-content", alignSelf: "start" }}>
          {/* Smart Checks */}
          <div style={{ backgroundColor: "#11111f", borderRadius: "16px", padding: "24px", border: "1px solid #1f2937", marginBottom: "24px" }}>
            <h3 style={{ fontSize: "20px", fontWeight: "bold", color: "#ffffff", marginBottom: "16px" }}>Smart Checks</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {smartChecks.map((check, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  {getStatusIcon(check.status)}
                  <div>
                    <p style={{ fontWeight: "500", color: "#ffffff" }}>{check.title}</p>
                    <p style={{ fontSize: "14px", color: "#9ca3af" }}>{check.desc}</p>
                    {check.desc2 && <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>{check.desc2}</p>}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "24px", paddingTop: "24px", borderTop: "1px solid #1f2937" }}>
              <h4 style={{ fontWeight: "600", color: "#ffffff", marginBottom: "12px" }}></h4>
              <div style={{ textAlign: "center" }}>
                <p style={{ fontSize: "60px", fontWeight: "bold", color: "#86efac", marginBottom: "8px" }}></p>
                <p style={{ fontSize: "14px", color: "#9ca3af" }}>Weighted average confidence across all proofs</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
            <button style={{ backgroundColor: "#16a34a", color: "#ffffff", padding: "16px", borderRadius: "12px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", border: "none", cursor: "pointer" }}>
              <CheckCircle size={20} /> Approve All Proofs
            </button>
            <button style={{ backgroundColor: "#ca8a04", color: "#ffffff", padding: "16px", borderRadius: "12px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", border: "none", cursor: "pointer" }}>
              <RefreshCw size={20} /> Request Reupload
            </button>
            <button style={{ backgroundColor: "#ea580c", color: "#ffffff", padding: "16px", borderRadius: "12px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", border: "none", cursor: "pointer" }}>
              <MessageSquare size={20} /> Comment to Employee
            </button>
            <button style={{ backgroundColor: "#dc2626", color: "#ffffff", padding: "16px", borderRadius: "12px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", border: "none", cursor: "pointer" }}>
              Reject Case
            </button>
          </div>

          {/* Exports */}
          <div style={{ backgroundColor: "#11111f", borderRadius: "16px", padding: "16px", border: "1px solid #1f2937" }}>
            <h4 style={{ fontWeight: "600", color: "#ffffff", marginBottom: "12px" }}>Exports</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <button style={{ backgroundColor: "#1f2937", color: "#ffffff", padding: "12px", borderRadius: "8px", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", border: "none", cursor: "pointer" }}>
                <Download size={16} /> Download Selected Proofs
              </button>
              <button style={{ backgroundColor: "#1f2937", color: "#ffffff", padding: "12px", borderRadius: "8px", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", border: "none", cursor: "pointer" }}>
                <File size={16} /> Export OCR Sheet (XLSX)
              </button>
              <button style={{ backgroundColor: "#1f2937", color: "#ffffff", padding: "12px", borderRadius: "8px", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", border: "none", cursor: "pointer" }}>
                <File size={16} /> Generate Case File (ZIP)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Chevron */}
      <div style={{ textAlign: "center", marginTop: "48px", paddingBottom: "48px" }}>
        <ChevronDown size={18} style={{ color: "#a78bfa" }} />
      </div>
    </div>
  );
};

export default DocumentDashboard;